/**
 * Forgot-password page (Phase 4, 2026-05-22).
 *
 * Customer enters their email; backend issues a 1-hour magic link and
 * emails it. UX always reports success - the backend deliberately
 * never confirms whether the email maps to an active account (defence
 * against enumeration).
 */

import { useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import { forgotPasswordApi, tenantApexFromHost, tenantSlugFromHost } from '@/lib/api';

export function ForgotPassword() {
  const initialSlug = tenantSlugFromHost();
  const apex = tenantApexFromHost();
  const [email, setEmail] = useState('');
  const [tenantSlugInput, setTenantSlugInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const tenantSlug = initialSlug ?? tenantSlugInput.trim().toLowerCase();
  const onApex = initialSlug === null;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email || !tenantSlug) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await forgotPasswordApi({ tenantSlug, email });
      setSent(true);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Request failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-8">
          <Mark variant="wordmark" size={44} />
          <h1 className="text-2xl font-bold tracking-tight">
            {sent ? 'Check your email' : 'Reset your password'}
          </h1>
          {!sent && tenantSlug && !onApex ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{tenantSlug}.{apex}</span>
            </p>
          ) : null}
        </div>

        {sent ? (
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border p-8 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="text-sm text-muted-foreground">
              If an account exists for <span className="font-mono">{email}</span>, we've sent a
              password-reset link. Check your inbox.
            </p>
            <p className="text-xs text-muted-foreground">
              The link expires in 1 hour. If you don't see it in 5 minutes, check spam.
            </p>
            <a
              href="/login"
              className="mt-2 inline-block text-sm text-primary hover:underline"
            >
              Back to sign-in
            </a>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-5 bg-white dark:bg-slate-900 rounded-lg shadow-sm border p-8"
          >
            {onApex ? (
              <div>
                <label htmlFor="tenantSlug" className="block text-sm font-medium mb-1">
                  Workspace
                </label>
                <div className="flex items-center gap-2 rounded-md border border-input bg-background pl-3 pr-3">
                  <input
                    id="tenantSlug"
                    type="text"
                    required
                    value={tenantSlugInput}
                    onChange={(e) =>
                      setTenantSlugInput(
                        e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      )
                    }
                    className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
                    placeholder="your-workspace"
                  />
                  <span className="text-sm text-muted-foreground">.{apex}</span>
                </div>
              </div>
            ) : null}

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoComplete="email"
                autoFocus
              />
            </div>

            {errorMsg ? (
              <div className="flex gap-2 items-start p-3 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || !email || !tenantSlug}
              className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Email me a reset link'
              )}
            </button>

            <p className="text-xs text-muted-foreground text-center pt-2">
              Remembered your password?{' '}
              <a href="/login" className="text-primary hover:underline">
                Sign in
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
